/**
 * WhatsApp Status Bot - Modal Wrapper per CoronaTek
 *
 * Integra il dashboard WhatsApp in una modale ResizableModal.
 * Configura automaticamente l'API base URL per il backend CoronaTek.
 *
 * @example
 * ```tsx
 * <WhatsAppStatusBotModal
 *   isOpen={showWhatsAppModal}
 *   onClose={() => setShowWhatsAppModal(false)}
 * />
 * ```
 */

import { useState, useEffect, useCallback } from 'react';
import { WhatsAppProvider } from './WhatsAppProvider';
import { WhatsAppDashboard } from './components/WhatsAppDashboard';
import ResizableModal from '../ui/ResizableModal';

interface HealthStatus {
  status: string;
  database: string;
  timestamp: string;
}

interface WhatsAppStatusBotModalProps {
  /** Controlla se la modale è aperta */
  isOpen: boolean;
  /** Callback per chiudere la modale */
  onClose: () => void;
  /** Titolo della modale (opzionale) */
  title?: string;
}

/**
 * Modal wrapper per il WhatsApp Status Bot.
 *
 * Configura automaticamente:
 * - apiBaseUrl: '/api/wa' (prefix per le route WhatsApp)
 * - refreshInterval: 10000ms (polling ogni 10 secondi)
 * - theme: 'dark' (tema scuro coerente con CoronaTek)
 */
export function WhatsAppStatusBotModal({
  isOpen,
  onClose,
  title = 'WhatsApp Status Bot'
}: WhatsAppStatusBotModalProps) {
  const [health, setHealth] = useState<HealthStatus | null>(null);

  const fetchHealth = useCallback(async () => {
    try {
      const response = await fetch('/api/wa/health');
      const data = await response.json();
      // Backend returns: { success: true, status: 'healthy', database: 'coronatek_whatsapp', ... }
      setHealth({
        status: (data.success && data.status === 'healthy') ? 'ok' : 'error',
        database: (data.success && data.database) ? 'connected' : 'disconnected',
        timestamp: data.timestamp || new Date().toISOString()
      });
    } catch (error) {
      console.error('[WhatsApp Bot] Error fetching health:', error);
      setHealth({
        status: 'error',
        database: 'disconnected',
        timestamp: new Date().toISOString()
      });
    }
  }, []);

  // Fetch health on mount and when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchHealth();
    }
  }, [isOpen, fetchHealth]);

  if (!isOpen) return null;

  return (
    <ResizableModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      initialWidth={1200}
      initialHeight={800}
      minWidth={800}
      minHeight={600}
      zIndex={200}
    >
      <WhatsAppProvider
        config={{
          apiBaseUrl: '/api/wa',
          refreshInterval: 10000,
          theme: 'dark'
        }}
      >
        <div className="h-full bg-slate-900">
          <WhatsAppDashboard
            health={health}
            onRefreshHealth={fetchHealth}
          />
        </div>
      </WhatsAppProvider>
    </ResizableModal>
  );
}

export default WhatsAppStatusBotModal;
